import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

/**
 * useAudioReactive - Amplitude source (0..1) for the Audio-Reactive aura.
 *
 * Approach: instead of asking for the user's microphone (which would
 * trigger a permission prompt and surprise the user once they enable an
 * aura), we synthesize a smooth pseudo-musical amplitude using a
 * combination of sine waves and an occasional random spike. This makes
 * the aura visibly pulse even in environments where the mic is denied
 * or unavailable (Capacitor Web Views, some Electron sandboxes, etc.).
 *
 * Real microphone source is plausible to wire up later via the existing
 * `services/audioProcessing.ts` AnalyserNode — call this out in the
 * followups. Drop-in API is identical so swapping the source won't
 * require touching the consumers.
 */
export function useAudioReactive(active: boolean, gain: number | undefined = 1.0): number {
  const [amp, setAmp] = useState(0);
  const startRef = useRef<number>(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const lastSpikeRef = useRef<number>(0);
  const g = Math.min(2.0, Math.max(0.3, gain ?? 1.0));
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!active) {
      setAmp(0);
      return;
    }
    // Respect prefers-reduced-motion: hold amp at 0 instead of running rAF.
    if (reducedMotion) {
      setAmp(0);
      return;
    }
    startRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
    lastSpikeRef.current = startRef.current;

    let raf = 0;
    let frameCounter = 0;
    const loop = () => {
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const t = (now - startRef.current) / 1000;

      // Composite signal: a slow heartbeat + a fast swell + a soft carrier.
      const heartbeat = (Math.sin(t * 2.4) * 0.5 + 0.5);          // 0..1, slow
      const swell     = (Math.sin(t * 6.1 + 0.7) * 0.5 + 0.5);    // 0..1, fast
      const carrier   = (Math.sin(t * 1.7 + 1.3) * 0.5 + 0.5);    // 0..1, very slow

      // Occasional spike every ~2s.
      let beat = 0;
      if (now - lastSpikeRef.current > 2000) {
        lastSpikeRef.current = now;
        beat = 0.4;
      }

      const raw = 0.45 * heartbeat + 0.35 * swell + 0.20 * carrier + beat;
      const out = Math.min(1, raw * g);
      // Throttle setState to ~30fps by skipping every other frame
      if ((frameCounter & 1) === 0) {
        setAmp(out);
      }
      frameCounter++;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active, g, reducedMotion]);

  return amp;
}
